import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Destructive and irreversible confirmations. Replaces `confirm()` entirely.
 *
 * The consequence is stated, not implied: "This removes 42 members from the group"
 * rather than "Are you sure?". For the most severe actions — closing a financial
 * period, deleting a church — the user types the record's name, because a muscle-memory
 * click on a familiar button is exactly how those get done by accident.
 */

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** One or two sentences naming what happens. */
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void | Promise<void>;
  destructive?: boolean;
  loading?: boolean;
  /** Requiring this makes the action typed-to-confirm. */
  typeToConfirm?: {
    /** Usually the record's name. */
    value: string;
    label: string;
  };
  closeLabel?: string;
}

export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  destructive = false,
  loading = false,
  typeToConfirm,
  closeLabel,
}: ConfirmDialogProps) => {
  const [typed, setTyped] = useState('');
  const [working, setWorking] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);

  // Reopening starts from empty, so a half-typed confirmation from a cancelled attempt
  // cannot be sitting there ready to be submitted.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (!open) setTyped('');
  }

  const confirmed =
    typeToConfirm === undefined ||
    typed.trim().toLowerCase() === typeToConfirm.value.trim().toLowerCase();

  const run = async () => {
    setWorking(true);
    try {
      await onConfirm();
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" closeLabel={closeLabel}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {typeToConfirm ? (
          <div className="space-y-1.5">
            <Label htmlFor="confirm-input">{typeToConfirm.label}</Label>
            <Input
              id="confirm-input"
              autoComplete="off"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={working}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'primary'}
            onClick={run}
            disabled={!confirmed}
            loading={loading || working}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
